import React, {useCallback, useEffect, useState} from 'react';
import { Form, Dropdown, Message, Segment, Dimmer, Loader } from 'semantic-ui-react';
import statesAbr from '../../../states';
import { useCustomers } from '../../../providers/CustomerProvider';
import { useAuth } from '../../../providers/AuthenticationProvider';
import { useDistributors } from '../../../providers/DistributorProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { customerService } from "../../../services/CustomerService";

export const CustomerEdit = () => {
    const { id } = useParams();
    const [companyName, setCompanyName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [addressOne, setAddressOne] = useState('');
    const [addressTwo, setAddressTwo] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [groupId, setGroupId] = useState('');
    const [licenseKey, setLicenseKey] = useState('');
    const [licenseKeyState, setLicenseKeyState] = useState('');
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiKeyLoading, setApiKeyLoading] = useState(false);
    const [distributorId, setDistributorId] = useState();
    const [distributorsList, setDistributorsList] = useState([]);
    const { isSystemAdmin, checkAuth, getUserType } = useAuth();
    const { getDistributors } = useDistributors();
    const { getCustomerById, editCustomer, deleteCustomer } = useCustomers();
    const navigate = useNavigate();
    const WEB_PAGE = 'Customers';

    useEffect(() => {
        checkAuth(getUserType(), WEB_PAGE) && navigate('/');

        const fetchDistributors = async () => {
            const { data: distributors } = await getDistributors(null, null, 'Name', 'asc');
            setDistributorsList(distributors.map((distributor) => {
                return {
                    id: distributor.id,
                    text: distributor.name,
                    value: distributor.id
                }
            }));
        }

        fetchDistributors();
    }, [getUserType, checkAuth, getDistributors, setDistributorsList, navigate]);

    const refreshCustomer = useCallback(async () => {
        const data = await getCustomerById(id);
        setCompanyName(data.companyName);
        setFirstName(data.firstname);
        setLastName(data.lastname);
        setEmail(data.email);
        setPhone(data.phone);
        setAddressOne(data.address_One);
        setAddressTwo(data.address_Two);
        setCity(data.city);
        setState(data.state);
        setZipcode(data.zip);
        setGroupId(data.groupId);
        setLicenseKey(data.apiKey);
        setLicenseKeyState(data.apiSubscriptionState);
        setDistributorId(data.distributorId);
        setLoading(false);
    }, [getCustomerById, setCompanyName, setFirstName, setLastName, setEmail, setPhone, setAddressOne, setAddressTwo, setCity, setState, setZipcode, setGroupId, setLicenseKey, setDistributorId, setLoading]);

    useEffect(() => {
        refreshCustomer();
    }, [id, refreshCustomer]);

    const handleSubmit = e => {
        e.preventDefault();
        if (phone.length !== 10) {
            setErrors(['Invalid phone number']);
            return;
        }
        const zipCodeValid = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zipcode);
        if (!zipCodeValid) {
            setErrors(['Invalid zip code']);
            return;
        }
        setLoading(true);
        editCustomer(id, companyName, firstName, lastName, addressOne, addressTwo, state, city, zipcode, email, phone, distributorId, groupId)
            .then(res => {
                setLoading(false);
                navigate('/customers');
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to update customer.']);
                }
                setLoading(false);
            });
    };

    const handleCreateApiKey = async (e) => {
        e.preventDefault();
        setApiKeyLoading(true);
        await customerService.createApiKey(id);
        setTimeout(async () => {
            await refreshCustomer();
            setApiKeyLoading(false);
        }, 5000);
    }

    const handleToggleApiSubscriptionState = async (e) => {
        e.preventDefault();
        setApiKeyLoading(true);
        let state = 'active';
        if (licenseKeyState === 'active') {
            state = 'suspended';
        }
        await customerService.toggleApiSubscription(id, state);
        await refreshCustomer();
        setApiKeyLoading(false);
    }

    return (
        <div>
            <div className="pageHeader">
                <h1>Edit Customer</h1>
            </div>

            <Segment style={{border: 'none', boxShadow: 'none', margin: '0', padding: '0'}}>
                <Dimmer active={loading} inverted>
                    <Loader active={loading} size="massive" inline />
                </Dimmer>
                <Form onSubmit={handleSubmit} error={errors.length > 0} style={{ maxWidth: '800px' }}>
                    <Form.Group>
                        <Form.Input
                            width={16}
                            label="Company Name"
                            defaultValue={companyName}
                            onChange={e => {
                                setCompanyName(e.target.value);
                            }}
                            type="text"
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Contact First Name"
                            defaultValue={firstName}
                            onChange={e => {
                                setFirstName(e.target.value);
                            }}
                            type="text"
                            required
                        />
                        <Form.Input
                            label="Contact Last Name"
                            defaultValue={lastName}
                            onChange={e => {
                                setLastName(e.target.value);
                            }}
                            type="text"
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Primary Contact Email"
                            defaultValue={email}
                            type={'email'}
                            onChange={e => {
                                setEmail(e.target.value);
                            }}
                            required
                        />
                        <Form.Input
                            label="Primary Contact Phone Number"
                            defaultValue={phone}
                            type="phone"
                            onChange={e => {
                                if (e.target.value == null || e.target.value === '') return;
                                let val = e.target.value.replace(/\D/g, '');
                                if (val.length > 10) {
                                    val = val.substring(0, 10);
                                }
                                e.target.value = val;
                                setPhone(e.target.value);
                            }}
                            required
                        />
                    </Form.Group>
                    <Form.Group>
                        {
                            isSystemAdmin() ?
                                <Form.Field
                                    label={'Distributor'}
                                    control={Dropdown}
                                    value={distributorId}
                                    fluid
                                    selection
                                    search
                                    options={distributorsList}
                                    style={{ height: '38px' }}
                                    onChange={(e, selected) => {
                                        setDistributorId(selected.value);
                                    }}
                                    width={16}
                                />
                                : null
                        }
                    </Form.Group>
                    <Form.Group>
                        <Form.Input
                            width={16}
                            label="Address Line 1"
                            defaultValue={addressOne}
                            onChange={e => {
                                setAddressOne(e.target.value);
                            }}
                            type="text"
                            required
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Input
                            width={16}
                            label="Address Line 2"
                            defaultValue={addressTwo}
                            onChange={e => {
                                setAddressTwo(e.target.value);
                            }}
                            type="text"
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input
                            label="City"
                            defaultValue={city}
                            onChange={e => {
                                setCity(e.target.value);
                            }}
                            required
                        />
                        <Form.Input
                            label={'State'}
                            value={state}
                            control={Dropdown}
                            fluid
                            selection
                            search
                            options={statesAbr}
                            style={{ height: '38px' }}
                            onChange={(e, selected) => {
                                setState(selected.value);
                            }}
                            required
                        />
                        <Form.Input
                            label="Zipcode"
                            defaultValue={zipcode}
                            onChange={e => {
                                setZipcode(e.target.value);
                            }}
                            required
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Input
                            label="Group Id"
                            defaultValue={groupId}
                            onChange={e => {
                                setGroupId(e.target.value);
                            }}
                        />
                        <Form.Input
                            label="License Key"
                            defaultValue={licenseKey}
                            readOnly
                        />
                        {
                            !licenseKey ? (
                                <Form.Button
                                    width={2}
                                    color={'blue'}
                                    style={{ marginTop: '25px' }}
                                    onClick={handleCreateApiKey}
                                    loading={apiKeyLoading}
                                    disabled={apiKeyLoading || licenseKey}
                                >
                                    New Key
                                </Form.Button>
                            ) : (
                                <Form.Button
                                    width={2}
                                    color={licenseKeyState === 'active' ? 'red' : 'green'}
                                    style={{ marginTop: '25px' }}
                                    onClick={handleToggleApiSubscriptionState}
                                    loading={apiKeyLoading}
                                >
                                    {licenseKeyState === 'active' ? 'Disable' : 'Enable'}
                                </Form.Button>
                            )
                        }
                    </Form.Group>

                    <Form.Group width={6}>
                        <Form.Button size={'large'} style={{ marginTop: '32px' }} type={'submit'} color={'blue'}>
                            Update
                        </Form.Button>
                        <Form.Button
                            size={'large'}
                            style={{ marginTop: '32px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/customers');
                            }}
                        >
                            Cancel
                        </Form.Button>
                        <Form.Button
                            size={'large'}
                            style={{ marginTop: '32px' }}
                            color={'red'}
                            onClick={(e) => {
                                e.preventDefault();
                                deleteCustomer(id).then(() => {
                                    navigate('/customers');
                                });
                            }}
                        >
                            Delete
                        </Form.Button>
                    </Form.Group>
                    {errors.length > 0 ? (
                        <Form.Group widths={2}>
                            <Message error list={errors} />
                        </Form.Group>
                    ) : null}
                </Form>
            </Segment>
        </div>
    );
};
